import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";
import Dropdown from "../components/ui/Dropdown";
import Badge from "../components/ui/Badge";
import Loader from "../components/ui/Loader";
import { apiService } from "../utils/apiService";
import { useLoadingState } from "../hooks/useLoadingState";

const Leaderboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leaderboard, setLeaderboard] = useState([]);
  const [error, setError] = useState("");
  const [week, setWeek] = useState("season");
  const [league, setLeague] = useState("all");
  const [leagues, setLeagues] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const isMobile = useIsMobile();
  const { loading, withLoading } = useLoadingState(true);

  // Initialize state from URL parameters
  useEffect(() => {
    const weekParam = searchParams.get("week") || "season";
    const leagueParam = searchParams.get("league") || "all";
    setWeek(weekParam);
    setLeague(leagueParam);
  }, [searchParams]);

  // Check if user should see league dropdown
  const shouldShowLeagueDropdown = leagues.length > 2; // More than "All Leagues" + 1 actual league

  const fetchLeaderboard = useCallback(async () => {
    try {
      await withLoading(async () => {
        setError(""); // Clear any previous errors
        let url = "/picks/leaderboard/overall";

        // Ensure week is a string value, not an object
        const weekValue = typeof week === "object" ? week.value : week;

        if (weekValue !== "season") {
          url = `/picks/leaderboard/week/${weekValue}`;
        }

        // Ensure league is a string value, not an object
        const leagueValue = typeof league === "object" ? league.value : league;

        // Add league parameter if not 'all'
        if (leagueValue !== "all") {
          const separator = url.includes("?") ? "&" : "?";
          url += `${separator}leagueId=${leagueValue}`;
        }

        const response = await apiService.get(url);

        setLeaderboard(response.leaderboard || response);
      });
    } catch (err) {
      apiService.handleError(err, setError, "Failed to load leaderboard");
    }
  }, [week, league, withLoading]);

  const fetchLeagues = async () => {
    try {
      // Add authentication headers
      const response = await apiService.get("/leagues");

      // Backend returns { leagues: [...] }, so we need to access response.data.leagues
      const leaguesData = response.leagues || response;
      const leagueOptions = leaguesData.map((l) => ({
        value: l.id,
        label: l.name,
      }));
      setLeagues([{ value: "all", label: "All Leagues" }, ...leagueOptions]);
    } catch (err) {
      // Set a fallback option if leagues fail to load
      setLeagues([{ value: "all", label: "All Leagues" }]);
    }
  };

  const generateWeekOptions = () => {
    const weekOptions = [];
    weekOptions.push({ value: "season", label: "Full Season" });
    for (let i = 1; i <= 22; i++) {
      weekOptions.push({ value: i.toString(), label: `Week ${i}` });
    }
    setWeeks(weekOptions);
  };

  // Safe onChange handlers that ensure we get string values and update URL
  const handleWeekChange = (value) => {
    setWeek(value);

    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    newParams.set("week", value);
    setSearchParams(newParams);
  };

  const handleLeagueChange = (value) => {
    setLeague(value);

    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    newParams.set("league", value);
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchLeagues();
    generateWeekOptions();
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size="lg" className="text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  // Mobile view
  if (isMobile) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-xl font-bold text-center">Leaderboard</h1>
        </div>

        {/* Filters */}
        <div
          className={`grid ${shouldShowLeagueDropdown ? "grid-cols-2" : "grid-cols-1"} gap-3 mb-4`}
        >
          {shouldShowLeagueDropdown && (
            <div>
              <Dropdown
                label="League"
                value={league}
                onChange={handleLeagueChange}
                options={leagues}
              />
            </div>
          )}
          <div>
            <Dropdown
              label="Period"
              value={week}
              onChange={handleWeekChange}
              options={weeks}
            />
          </div>
        </div>

        {/* Mobile Leaderboard Cards */}
        <div className="flex-1 overflow-y-auto space-y-3">
          {leaderboard.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <div className="text-6xl mb-4">🏆</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Leaderboard Yet</h3>
              <p className="text-sm text-center">
                {week === "season" 
                  ? "No games have been completed yet. Check back after some games finish!"
                  : `No games have been completed for Week ${week} yet. Check back after some games finish!`
                }
              </p>
            </div>
          ) : (
            leaderboard.map((entry, index) => (
              <div
                key={entry.userId}
                className="bg-white rounded-lg border border-gray-200 shadow-sm p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {index === 0 ? (
                        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          1
                        </div>
                      ) : index === 1 ? (
                        <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          2
                        </div>
                      ) : index === 2 ? (
                        <div className="w-8 h-8 bg-orange-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          3
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {entry.userName || entry.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {entry.totalCorrect || 0} of {entry.totalPicks || 0}{" "}
                        correct
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">
                      {entry.winPercentage}%
                    </div>
                    <div className="text-xs text-gray-500">Win Rate</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Desktop view
  return (
    <>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-4">Leaderboard</h1>
          <p className="text-gray-600 mb-8">
            See how you stack up against the family in this week&apos;s NFL
            pick&apos;em!
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 md:mt-0">
          {shouldShowLeagueDropdown && (
            <Dropdown
              label="League"
              value={league}
              onChange={handleLeagueChange}
              options={leagues}
              className="w-full sm:w-48"
            />
          )}
          <Dropdown
            label="Time Period"
            value={week}
            onChange={handleWeekChange}
            options={weeks}
            className="w-full sm:w-48"
          />
        </div>
      </div>

      {leaderboard.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12">
          <div className="flex flex-col items-center justify-center text-gray-500">
            <div className="text-6xl mb-4">🏆</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Leaderboard Yet</h3>
            <p className="text-center max-w-md">
              {week === "season" 
                ? "No games have been completed yet. Check back after some games finish to see the leaderboard!"
                : `No games have been completed for Week ${week} yet. Check back after some games finish to see the leaderboard!`
              }
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Correct
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaderboard.map((entry, index) => (
                  <tr key={entry.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {index === 0 ? (
                        <Badge color="gold">1st</Badge>
                      ) : index === 1 ? (
                        <Badge color="silver">2nd</Badge>
                      ) : index === 2 ? (
                        <Badge color="bronze">3rd</Badge>
                      ) : (
                        `${index + 1}th`
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.userName || entry.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.totalCorrect}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.totalPicks}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.winPercentage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default Leaderboard;
